<template>
  <div class="aa-checkout ">
    <v-container>
      <div class="aa-checkout__heading">
        <h1>
          Checkout
        </h1>
        <additional-products-dialog v-show="this.authUser" />
      </div>

      <div v-if="!this.localDataItems && !this.cartFilterLength">
        You have no items in your cart. Please return to
        <router-link
          to="/art-categories"
          style="color:var(--red); text-decoration: underline;"
          >Art Categories</router-link
        >
        to add items to your cart.
      </div>

      <div
        v-if="this.cartFilterLength || this.localDataItems"
        class="aa-checkout__main"
      >
        <div class="aa-checkout__main-items">
          <div class="aa-checkout__main-items-length">
            {{ cart.total_items || this.localDataQuanty }} item<span
              v-if="cart.total_items !== 1"
              >s
            </span>
          </div>
          <cart-item-card
            @events="isFlag"
            :item="item"
            v-for="item in cart.cart_items || this.localDataItems"
            :key="item.id"
          />
        </div>

        <div class="aa-checkout__main-info">
          <!-- Guest Checkout Option -->
          <div v-show="!this.authUser && this.localDataItems">
            <v-card flat class="mb-4 pa-4">
              <h3 class="mb-3">Checkout Options</h3>
              
              <v-radio-group v-model="checkoutType" class="mt-0">
                <v-radio 
                  label="Continue as Guest" 
                  value="guest"
                  class="mb-2"
                ></v-radio>
                <p class="ml-8 text-caption grey--text mb-4">
                  Quick checkout without creating an account
                </p>

                <v-radio 
                  label="Login / Create Account" 
                  value="account"
                  class="mt-2"
                ></v-radio>
                <p class="ml-8 text-caption grey--text">
                  Save your info for faster future checkouts
                </p>
              </v-radio-group>
            </v-card>

            <!-- Guest Information Form -->
            <v-card v-if="checkoutType === 'guest'" flat class="mb-4">
              <h3>Guest Information</h3>
              <div class="aa-checkout__main-info-shipping">
                
                <label>
                  <h4>Email Address</h4>
                  <v-text-field
                    type="email"
                    placeholder="Email Address"
                    v-model="guestEmail"
                    :rules="emailRules"
                    solo
                    flat
                    hide-details="auto"
                  />
                </label>

                <label>
                  <h4>Password (Optional)</h4>
                  <v-text-field
                    type="password"
                    placeholder="Create a password to save your account"
                    v-model="guestPassword"
                    solo
                    flat
                    hide-details="auto"
                  />
                  <p class="text-caption grey--text mt-1 mb-0">
                    If you provide a password, you'll be able to log in later to track your order and place future orders faster.
                  </p>
                </label>

                <label class="flex-6">
                  <h4>First Name</h4>
                  <v-text-field
                    type="text"
                    placeholder="First Name"
                    v-model="guestFirstName"
                    :rules="requiredRules"
                    solo
                    flat
                    hide-details="auto"
                  />
                </label>

                <label class="flex-6">
                  <h4>Last Name</h4>
                  <v-text-field
                    type="text"
                    placeholder="Last Name"
                    v-model="guestLastName"
                    :rules="requiredRules"
                    solo
                    flat
                    hide-details="auto"
                  />
                </label>

                <label>
                  <h4>Phone Number (Optional)</h4>
                  <v-text-field
                    type="text"
                    placeholder="Phone Number"
                    v-model="guestPhone"
                    solo
                    flat
                    hide-details="auto"
                  />
                </label>

              </div>
            </v-card>

            <!-- Login/Register Tabs -->
            <div class="aa-checkout__main-tabs" v-if="checkoutType === 'account'">
              <v-alert dense outlined class="aa-mark">
                Please login or register to add the shipping/billing information.
              </v-alert>

              <v-tabs v-model="tab">
                <v-tab href="#login" :ripple="false" :disabled="this.loading">
                  Log In
                </v-tab>

                <v-tab href="#signup" :ripple="false" :disabled="this.loading">
                  Register
                </v-tab>

                <v-tab style="display: none" href="#forgot-password"></v-tab>
              </v-tabs>

              <v-tabs-items v-model="tab">
                <v-tab-item value="login">
                  <LoginForm
                    :isReset="isReset"
                    :isFlagPath="isFlagPath"
                    @upDataAuth="upDataAuthFlag"
                  />
                </v-tab-item>

                <v-tab-item value="signup">
                  <registration-form
                    :isFlagPath="isFlagPath"
                    @upDataAuth="upDataAuthFlag"
                  />
                </v-tab-item>

                <v-tab-item value="forgot-password">
                  <forgot-password-form />
                </v-tab-item>
              </v-tabs-items>
            </div>
          </div>

          <div
            class="aa-checkout__main-info-wrap "
            :class="{ disabled: (!this.authUser && this.localDataItems && checkoutType === 'account') }"
          >
            <v-form v-model="formValid" ref="form" lazy-validation>
              <v-card flat>
                <h3>Shipping Address</h3>
                <div class="aa-checkout__main-info-shipping">

                  <label>
                    <h4>Name of Recipient</h4>
                    <v-text-field
                        type="text"
                        placeholder="Name of Recipient"
                        v-model="shipping_address_name"
                        :rules="validationRules.required"
                        solo
                        flat
                        hide-details="auto"
                    />
                  </label>

                  <label>
                    <h4>Address Line 1</h4>
                    <v-text-field
                      type="text"
                      placeholder="Address Line 1"
                      v-model="shipping_address_line_1"
                      :rules="validationRules.required"
                      solo
                      flat
                      hide-details="auto"
                    />
                  </label>

                  <label>
                    <h4>Address Line 2</h4>
                    <v-text-field
                      type="text"
                      placeholder="Address Line 2"
                      v-model="shipping_address_line_2"
                      solo
                      flat
                      hide-details="auto"
                    />
                  </label>

                  <label class="flex-6">
                    <h4>City</h4>
                    <v-text-field
                      type="text"
                      placeholder="City"
                      v-model="shipping_address_city"
                      :rules="validationRules.required"
                      solo
                      flat
                      hide-details="auto"
                    />
                  </label>

                  <label class="flex-6">
                    <h4>State</h4>
                    <v-text-field
                      type="text"
                      placeholder="State"
                      v-model="shipping_address_state"
                      :rules="validationRules.required"
                      solo
                      flat
                      hide-details="auto"
                  /></label>

                  <label>
                    <h4>Postal Code</h4>
                    <v-text-field
                      type="text"
                      placeholder="Postal Code"
                      v-model="shipping_address_postal_code"
                      :rules="validationRules.required"
                      solo
                      flat
                      hide-details="auto"
                  /></label>
                </div>
              </v-card>
              <v-card flat>
                <h3>Billing Address</h3>
                <v-checkbox
                  v-model="shipping_is_billing"
                  label="Same as Shipping"
                  hide-details="auto"
                  :ripple="false"
                  color="red"
                  style="margin-bottom: 20px;"
                ></v-checkbox>
                <div
                  class="aa-checkout__main-info-billing"
                  v-if="!shipping_is_billing"
                >
                  <label>
                    <h4>Billing Name</h4>
                    <v-text-field
                        type="text"
                        placeholder="Billing Name"
                        v-model="billing_name"
                        solo
                        flat
                        hide-details="auto"
                    /></label>
                  <label>
                    <h4>Address Line 1</h4>
                    <v-text-field
                      type="text"
                      placeholder="Address Line 1"
                      v-model="billing_address_line_1"
                      :rules="validationRules.requiredIfNotSame"
                      solo
                      flat
                      hide-details="auto"
                  /></label>

                  <label>
                    <h4>Address Line 2</h4>
                    <v-text-field
                      type="text"
                      placeholder="Address Line 2"
                      v-model="billing_address_line_2"
                      solo
                      flat
                      hide-details="auto"
                    />
                  </label>

                  <label class="flex-6">
                    <h4>City</h4>
                    <v-text-field
                      type="text"
                      placeholder="City"
                      v-model="billing_address_city"
                      solo
                      flat
                      hide-details="auto"
                      :rules="validationRules.requiredIfNotSame"
                    />
                  </label>

                  <label class="flex-6">
                    <h4>State</h4>
                    <v-text-field
                      type="text"
                      placeholder="State"
                      v-model="billing_address_state"
                      solo
                      flat
                      hide-details="auto"
                      :rules="validationRules.requiredIfNotSame"
                    />
                  </label>

                  <label>
                    <h4>Postal Code</h4>
                    <v-text-field
                      type="text"
                      placeholder="Postal Code"
                      v-model="billing_address_postal_code"
                      solo
                      flat
                      hide-details="auto"
                      :rules="validationRules.requiredIfNotSame"
                    />
                  </label>
                </div>
              </v-card>
              <v-card flat>
                <h3>Additional Instructions</h3>
                <label>
                  <p class="ma-0" style="color: #000000;">
                    Enter any additional instructions if required
                  </p>
                  <v-textarea
                    type="text"
                    v-model="additional_instructions"
                    rows="3"
                    solo
                    flat
                    hide-details="auto"
                    class="aa-checkout__additional-instructions"
                  />
                </label>
              </v-card>
              <v-card flat>
                <h3>Payment</h3>
                <div class="aa-checkout__main-info-shipping">
                  <label>
                    <h4>Name on Card</h4>
                    <v-text-field
                      type="text"
                      placeholder="Name on Card"
                      v-model="card_name"
                      autocomplete="cc-name"
                      :rules="validationRules.required"
                      solo
                      flat
                      hide-details="auto"
                    />
                  </label>

                  <label>
                    <h4>Card Number</h4>
                    <v-text-field
                      type="tel"
                      placeholder="Card Number"
                      v-model="card_number"
                      autocomplete="cc-number"
                      :rules="validationRules.required"
                      solo
                      flat
                      hide-details="auto"
                    />
                  </label>

                  <label class="flex-6">
                    <h4>Exp. Month</h4>
                    <v-text-field
                      type="tel"
                      placeholder="MM"
                      v-model="card_exp_month"
                      autocomplete="cc-exp-month"
                      :rules="validationRules.required"
                      solo
                      flat
                      hide-details="auto"
                    />
                  </label>

                  <label class="flex-6">
                    <h4>Exp. Year</h4>
                    <v-text-field
                      type="tel"
                      placeholder="YYYY"
                      v-model="card_exp_year"
                      autocomplete="cc-exp-year"
                      :rules="validationRules.required"
                      solo
                      flat
                      hide-details="auto"
                    />
                  </label>

                  <label>
                    <h4>CVC</h4>
                    <v-text-field
                      type="tel"
                      placeholder="CVC"
                      v-model="card_cvc"
                      autocomplete="cc-csc"
                      :rules="validationRules.required"
                      solo
                      flat
                      hide-details="auto"
                    />
                  </label>
                </div>
                <p v-if="cardError" class="aa-checkout__card-error">
                  {{ cardError }}
                </p>
              </v-card>
              <v-card flat class="aa-checkout__subtotal">
                <div class="aa-checkout__subtotal-alert">
                  <span>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        opacity="0.4"
                        cx="12"
                        cy="12"
                        r="11.6667"
                        fill="black"
                      />
                      <path
                        d="M10.8333 17.8333C10.8333 17.189 11.3557 16.6667 12 16.6667C12.6443 16.6667 13.1667 17.189 13.1667 17.8333C13.1667 18.4777 12.6443 19 12 19C11.3557 19 10.8333 18.4777 10.8333 17.8333Z"
                        fill="black"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M12 15.2083C11.5168 15.2083 11.125 14.8166 11.125 14.3333L11.125 6.16667C11.125 5.68342 11.5168 5.29167 12 5.29167C12.4832 5.29167 12.875 5.68342 12.875 6.16667L12.875 14.3333C12.875 14.8166 12.4832 15.2083 12 15.2083Z"
                        fill="black"
                      />
                    </svg>
                  </span>
                  Shipping is free. The price below is your final total,
                  charged now.
                </div>

                <div
                  class="aa-checkout__subtotal-discount"
                  v-if="cart.discount_total"
                >
                  <div>
                    <h4>Product Total</h4>
                    <span class="price">{{
                      cart.product_total | currency
                    }}</span>
                  </div>
                  <div>
                    <h4>Discount</h4>
                    <span class="price"
                      >-{{ cart.discount_total | currency }}
                    </span>
                  </div>
                </div>

                <div class="aa-checkout__subtotal-price">
                  <div>
                    <h4>Subtotal</h4>
                    <span class="price">
                      {{
                        cart.grand_total
                          ? cart.grand_total
                          : localDataSubtotal | currency
                      }}
                    </span>
                  </div>
                  <v-btn
                    @click="submit"
                    :loading="loading"
                    :disabled="loading"
                    class="btn btn-primary arrow"
                  >
                    Submit Order
                  </v-btn>
                </div>
              </v-card>
            </v-form>
          </div>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import LoginForm from "@/components/Form/LoginForm.vue";
import RegistrationForm from "@/components/Form/RegistrationForm.vue";
import ForgotPasswordForm from "@/components/Form/ForgotPasswordForm.vue";
import CartItemCard from "@/components/ToolsBar/CartItemCard.vue";
import OrderService from "@/services/OrderService";
import PaymentService from "@/services/PaymentService";
import AdditionalProductsDialog from "@/components/ToolsBar/AdditionalProductsDialog.vue";
import { trackEvent } from "@/utils/analytics";
export default {
  name: "Checkout",
  components: {
    CartItemCard,
    AdditionalProductsDialog,
    LoginForm,
    RegistrationForm,
    ForgotPasswordForm,
  },
  mounted: async function() {
    await this.$store.dispatch("cart/getCart");

    if (
      this.authUser ||
      window.localStorage.getItem("guest") === "isNotGuest"
    ) {
      this.upDataAuthFlag();
    } else {
      const dataGuest =
        JSON.parse(localStorage.getItem("guestCart") || "null") || {};

      this.localDataItems = dataGuest.cart_items || null;
      this.localDataSubtotal = dataGuest.product_total || 0;
      this.localDataQuanty = dataGuest.total_items || 0;
    }

    this.trackBeginCheckout();
  },
  data: () => {
    return {
      checkoutType: 'guest', // 'guest' or 'account'
      guestEmail: '',
      guestPassword: '',
      guestFirstName: '',
      guestLastName: '',
      guestPhone: '',
      billing_name: null,
      isFlagPath: true,
      isReset: false,
      tab: null,
      localDataSubtotal: 0,
      localDataQuanty: 0,
      localDataItems: null,
      shipping_is_billing: true,
      formValid: true,
      loading: false,
      card_name: null,
      card_number: null,
      card_exp_month: null,
      card_exp_year: null,
      card_cvc: null,
      cardError: null,
      shipping_address_name: null,
      shipping_address_line_1: null,
      shipping_address_line_2: null,
      shipping_address_city: null,
      shipping_address_state: null,
      shipping_address_postal_code: null,
      shipping_address_country: null,
      billing_address_line_1: null,
      billing_address_line_2: null,
      billing_address_city: null,
      billing_address_state: null,
      billing_address_postal_code: null,
      billing_address_country: null,
      additional_instructions: null,
      countries: [
        { value: "US", text: "United States" },
        { value: "CN", text: "Canada" },
      ],
    };
  },
  methods: {
    checkoutEventPayload: function() {
      const items = this.cart.cart_items || this.localDataItems || [];
      const value = this.cart.grand_total || this.localDataSubtotal || 0;
      return {
        currency: "USD",
        value,
        items: items.map((item) => ({
          item_id: String(item.id),
          item_name: item.description,
          quantity: item.quantity,
          price: item.quantity
            ? Number((item.total / item.quantity).toFixed(2))
            : item.total,
        })),
      };
    },
    trackBeginCheckout: function() {
      const payload = this.checkoutEventPayload();
      if (!payload.items.length) {
        return;
      }
      trackEvent("begin_checkout", payload);
    },
    upDataAuthFlag: async function() {
      await this.$store.dispatch("auth/getAuthUser").then((user) => {
        this.shipping_address_name = user.shipping_address_name;
        this.shipping_address_line_1 = user.shipping_address_line_1;
        this.shipping_address_line_2 = user.shipping_address_line_2;
        this.shipping_address_city = user.shipping_address_city;
        this.shipping_address_state = user.shipping_address_state;
        this.shipping_address_postal_code = user.shipping_address_postal_code;
        if (user.shipping_address_country) {
          this.shipping_address_country = this.countries.find((country) => {
            return country.value === user.shipping_address_country;
          });
        }
        this.billing_name = user.billing_address_name;
        this.billing_address_line_1 = user.billing_address_line_1;
        this.billing_address_line_2 = user.billing_address_line_2;
        this.billing_address_city = user.billing_address_city;
        this.billing_address_state = user.billing_address_state;
        this.billing_address_postal_code = user.billing_address_postal_code;
        if (user.billing_address_country) {
          this.billing_address_country = this.countries.find((country) => {
            return country.value === user.billing_address_country;
          });
        }
      });
    },
    isFlag: async function() {
      const dataGuest = await JSON.parse(
        localStorage.getItem("guestCart") || "[]"
      );

      this.localDataItems = dataGuest.cart_items;
      this.localDataSubtotal = dataGuest.product_total;
      this.localDataQuanty = dataGuest.total_items;
    },
    submit: function() {
      // Validate guest information if guest checkout
      if (!this.authUser && this.checkoutType === 'guest') {
        if (!this.guestEmail || !this.guestFirstName || !this.guestLastName) {
          this.$store.dispatch("snackbar/showSnack", {
            text: "Please fill in all required guest information fields.",
            color: "error",
            timeout: 3000,
          });
          return;
        }
        
        // Validate email format
        if (!/^.+@.+\..+$/.test(this.guestEmail)) {
          this.$store.dispatch("snackbar/showSnack", {
            text: "Please enter a valid email address.",
            color: "error",
            timeout: 3000,
          });
          return;
        }
      }
      
      if (this.loading) {
        // Already submitting - a second click (double-click, or clicked again
        // before the button visually updates) must not fire a second request,
        // which would create a fully separate, real order and QuickBooks
        // invoice for the same cart.
        return;
      }

      const valid = this.$refs.form.validate();
      if (valid) {
        this.loading = true;
        this.cardError = null;
        trackEvent("add_payment_info", this.checkoutEventPayload());

        // Tokenize the card directly with Intuit first - raw card details
        // never reach our own server, only the resulting one-time token does.
        PaymentService.tokenizeCard({
          name: this.card_name,
          number: (this.card_number || "").replace(/\s+/g, ""),
          expMonth: this.card_exp_month,
          expYear: this.card_exp_year,
          cvc: this.card_cvc,
          address: {
            streetAddress: this.shipping_is_billing
              ? this.shipping_address_line_1
              : this.billing_address_line_1,
            city: this.shipping_is_billing
              ? this.shipping_address_city
              : this.billing_address_city,
            region: this.shipping_is_billing
              ? this.shipping_address_state
              : this.billing_address_state,
            postalCode: this.shipping_is_billing
              ? this.shipping_address_postal_code
              : this.billing_address_postal_code,
          },
        })
          .then((tokenResult) => {
            this.submitOrderWithCardToken(tokenResult.value);
          })
          .catch((err) => {
            console.error(err);
            this.cardError =
              "We couldn't process your card. Please check your card details and try again.";
            // Nothing else on the form is touched, so the customer can fix
            // the card and resubmit without retyping their address.
            this.loading = false;
          });
      }
    },
    submitOrderWithCardToken: function(cardToken) {
      // Prepare order data
      const orderData = {
        billing_name: this.billing_name,
        shipping_is_billing: this.shipping_is_billing,
        shipping_address_name: this.shipping_address_name,
        shipping_address_line_1: this.shipping_address_line_1,
        shipping_address_line_2: this.shipping_address_line_2,
        shipping_address_city: this.shipping_address_city,
        shipping_address_state: this.shipping_address_state,
        shipping_address_postal_code: this.shipping_address_postal_code,
        shipping_address_country: this.shipping_address_country,
        billing_address_line_1: this.billing_address_line_1,
        billing_address_line_2: this.billing_address_line_2,
        billing_address_city: this.billing_address_city,
        billing_address_state: this.billing_address_state,
        billing_address_postal_code: this.billing_address_postal_code,
        billing_address_country: this.billing_address_country,
        additional_instructions: this.additional_instructions,
        order_items: this.cart.cart_items || this.localDataItems,
        card_token: cardToken,
      };

      // Add guest information if guest checkout
      if (!this.authUser && this.checkoutType === 'guest') {
        orderData.guest_email = this.guestEmail;
        orderData.guest_first_name = this.guestFirstName;
        orderData.guest_last_name = this.guestLastName;
        orderData.guest_phone = this.guestPhone;
        if (this.guestPassword) {
          orderData.guest_password = this.guestPassword;
        }
      }

      // Submit the order
      OrderService.addOrder(orderData)
        .then((response) => {
          // Rare edge case: the card was authorized and the order/invoice
          // were created, but confirming payment against the invoice failed
          // afterward - the backend voids the authorization (the customer
          // isn't charged) and returns no order object here. There's nothing
          // to show on a ThankYou page in that case, so stay on checkout.
          if (!response.order) {
            this.$store.dispatch("snackbar/showSnack", {
              text: response.message,
              color: "warning",
              timeout: 8000,
            });
            this.loading = false;
            return;
          }

          console.log(response.order);
          this.$store.dispatch("snackbar/showSnack", {
            text: "Order submitted successfully.",
            color: "success",
            timeout: 2000,
          });

          // Clear local guest cart if it exists (for guest checkouts)
          if (this.localDataItems) {
            localStorage.removeItem("guestCart");
          }

          // Refresh cart for authenticated users
          if (this.authUser) {
            this.$store.dispatch("cart/getCart");
          }

          // Hide loader before redirecting
          this.$store.dispatch("loader/showLoader", {
            loading: false,
          });

          this.$router.push({
            name: "ThankYou",
            params: {
              order: response.order,
              guest_token: response.guest_token,
              is_guest: response.is_guest
            },
          });
        })
        .catch((err) => {
          console.error(err);

          // A declined/invalid card comes back from our backend as a
          // distinct shape - show it inline near the card fields instead of
          // the generic snackbar, so it's clear what to fix.
          if (err.response && err.response.status === 402 && err.response.data.card_error) {
            this.cardError = err.response.data.message;
            this.loading = false;
            return;
          }

          let errorMessage = "There was an error submitting order. Please try again later or contact support.";

          // Check if it's an email exists error
          if (err.response && err.response.data) {
            if (err.response.data.message && err.response.data.message.includes('email')) {
              errorMessage = err.response.data.message;
            } else if (err.response.data.errors && err.response.data.errors.guest_email) {
              errorMessage = err.response.data.errors.guest_email[0];
            }
          }

          this.$store.dispatch("snackbar/showSnack", {
            text: errorMessage,
            color: "error",
            timeout: 5000,
          });

          // Re-enable the button so a real failure (as opposed to the
          // duplicate-order case above, which never reaches this far) can
          // actually be retried.
          this.loading = false;
        });
    },
  },
  computed: {
    ...mapGetters("cart", ["error", "cart"]),
    ...mapGetters("auth", ["error", "authUser"]),
    cartData() {
      if (this.cart) {
        return this.cart;
      }
      return false;
    },
    validationRules: function() {
      const rules = {};
      rules.required = [(v) => !!v || "This field is required."];
      rules.requiredIfNotSame = [];
      if (!this.shipping_is_billing) {
        rules.requiredIfNotSame.push((v) => !!v || "This field is required.");
      }
      return rules;
    },
    emailRules: function() {
      return [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ];
    },
    requiredRules: function() {
      return [
        v => !!v || 'This field is required'
      ];
    },
    cartFilterLength: function() {
      if (this.cart.cart_items && this.cart.cart_items.length == 0) {
        console.log("checkout empty");
        return this.cart.cart_items.length;
      }

      return this.authUser ? true : false;
    },
  },
  filters: {
    currency: function(value) {
      if (!value) return "-";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
    },
  },
};
</script>

<style lang="scss">
@import "./style-checkout.scss";
</style>
